import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { SoundProvider } from './lib/sounds';
import { LoginPage } from './pages/LoginPage';
import { VerifyPage } from './pages/VerifyPage';
import { DashboardPage } from './pages/DashboardPage';
import {
  AdminLayout,
  OverviewPage,
  UsersPage,
  WorldsPage,
  UserDetailPage,
  WorldPage,
  QueuesPage,
  AuditLogPage,
  InboundMailPage,
  InboundMailDetailPage,
  OutboundMailPage,
  OutboundMailDetailPage,
  QAPage,
  AnalyticsPage,
  ThumbnailsPage,
} from './pages/admin';

// Redirect old world state route to new unified page
function WorldStateRedirect() {
  const { worldId } = useParams<{ worldId: string }>();
  return <Navigate to={`/admin/worlds/${worldId}#canon`} replace />;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-mist animate-pulse-slow">The gateway stirs...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-mist animate-pulse-slow">The gateway stirs...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Admin route (requires admin role)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-mist animate-pulse-slow">Verifying access...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <SoundProvider>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route path="/auth/verify" element={<VerifyPage />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:id" element={<UserDetailPage />} />
            <Route path="worlds" element={<WorldsPage />} />
            <Route path="worlds/:id" element={<WorldPage />} />
            <Route path="worlds/:worldId/state" element={<WorldStateRedirect />} />
            <Route path="queues" element={<QueuesPage />} />
            <Route path="qa" element={<QAPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="thumbnails" element={<ThumbnailsPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="letters-in" element={<InboundMailPage />} />
            <Route path="letters-in/:id" element={<InboundMailDetailPage />} />
            <Route path="letters-out" element={<OutboundMailPage />} />
            <Route path="letters-out/:id" element={<OutboundMailDetailPage />} />
          </Route>

          {/* Redirect root to login or dashboard */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 */}
          <Route
            path="*"
            element={
              <div className="min-h-screen bg-void flex items-center justify-center">
                <div className="text-center">
                  <h1 className="font-display text-2xl text-parchment mb-4">The Path is Lost</h1>
                  <p className="text-mist mb-6">This passage leads nowhere.</p>
                  <a href="/login" className="text-rune hover:text-rune-glow underline">
                    Return to the gateway
                  </a>
                </div>
              </div>
            }
          />
        </Routes>
      </AuthProvider>
    </SoundProvider>
  );
}
